import type {
  PlanDetail,
  PlanPackageMembershipReplaceRequest,
  PlanPackageMembershipReplaceResult,
  PlanReadPage,
  PlanWriteRequest,
  SolutionTree,
} from '../api/active/generated/plan';
import type { ActiveTransport } from '../api/active/transport';
import {
  type CatalogReadIndex,
  validateCatalogReadIndex,
} from '../quote-integration/catalogReadIndex';
import {
  notifyQuoteCatalogMutation,
  quoteCatalogSessionResource,
} from '../quote-integration/catalogSessionResource';
import type { PlanListQuery, PlanPort } from './planPort';

export class PlanApiError extends Error {
  constructor(
    readonly status: number | null,
    readonly code: string | undefined,
    message: string,
    readonly currentResourceVersion?: string | null,
  ) {
    super(message);
  }
}

async function problem(response: Response): Promise<PlanApiError> {
  let value: {
    code?: string;
    errorCode?: string;
    detail?: string;
    title?: string;
    currentResourceVersion?: string | null;
  } = {};
  try {
    value = (await response.json()) as typeof value;
  } catch {
    // Keep the status when an upstream proxy returns no problem body.
  }
  const code = value.code ?? value.errorCode;
  const detail =
    response.status === 503 &&
    code === 'DEPENDENCY_UNAVAILABLE' &&
    value.detail === 'The request could not be completed safely.'
      ? '暂无法完成方案操作，请稍后重试。'
      : (value.detail ??
        value.title ??
        `方案请求失败（HTTP ${response.status}）。`);
  return new PlanApiError(
    response.status,
    code,
    detail,
    value.currentResourceVersion,
  );
}

function queryString(query: PlanListQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.searchBy) params.set('searchBy', query.searchBy);
  if (query.status) params.set('status', query.status);
  return params.toString();
}

export function createPlanApi(
  transport: ActiveTransport,
  csrfToken: string | null,
): PlanPort {
  const mutationHeaders: Record<string, string> = csrfToken
    ? { 'X-CSRF-Token': csrfToken }
    : {};
  const read = async (response: Response) => {
    if (!response.ok) throw await problem(response);
    return (await response.json()) as PlanDetail;
  };
  return {
    async catalogIndex(signal) {
      const response = await transport.requestResponse('/plans/catalog-index', {
        signal,
        cache: 'no-store',
      });
      if (!response.ok) throw await problem(response);
      return validateCatalogReadIndex(
        (await response.json()) as CatalogReadIndex,
      );
    },
    async getSolutionTree(signal) {
      const response = await transport.requestResponse('/solution-tree', {
        signal,
        cache: 'no-store',
      });
      if (!response.ok) throw await problem(response);
      return (await response.json()) as SolutionTree;
    },
    async list(query, signal) {
      const response = await transport.requestResponse(
        `/plans?${queryString(query)}`,
        { signal, cache: 'no-store' },
      );
      if (!response.ok) throw await problem(response);
      return (await response.json()) as PlanReadPage;
    },
    async get(planId, signal) {
      const response = await transport.requestResponse(
        `/plans/${encodeURIComponent(planId)}`,
        { signal, cache: 'no-store' },
      );
      return {
        value: await read(response),
        etag: response.headers.get('ETag') ?? '',
      };
    },
    async create(body: PlanWriteRequest, idempotencyKey, signal) {
      const mutationScope = quoteCatalogSessionResource.currentScope();
      const response = await transport.requestResponse('/plans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          ...mutationHeaders,
        },
        body: JSON.stringify(body),
        signal,
      });
      const value = {
        value: await read(response),
        etag: response.headers.get('ETag') ?? '',
      };
      notifyQuoteCatalogMutation(
        mutationScope,
        'plans',
        [value.value.planId],
        ['packages'],
      );
      return value;
    },
    async remove(planId, etag, idempotencyKey, signal) {
      const mutationScope = quoteCatalogSessionResource.currentScope();
      const response = await transport.requestResponse(
        `/plans/${encodeURIComponent(planId)}`,
        {
          method: 'DELETE',
          headers: {
            'If-Match': etag,
            'Idempotency-Key': idempotencyKey,
            ...mutationHeaders,
          },
          signal,
        },
      );
      if (!response.ok) throw await problem(response);
      notifyQuoteCatalogMutation(
        mutationScope,
        'plans',
        [planId],
        ['packages'],
      );
    },
  };
}

export type PlanMembershipApiResponse = {
  body: PlanPackageMembershipReplaceRequest;
  result: PlanPackageMembershipReplaceResult;
};
